import React, { useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getAllTemplates } from '~/redux/actions/template-builder';
import { fetchOrderCost, creatOrder } from '~/redux/actions/orders-actions';
import { fetchReturnAddresses, fetchExpectedDeliveryDate } from '~/redux/actions/contacts-actions';
import { getUserData } from '~/redux/actions/auth-actions';

// Hooks
import useOrderPaymentGate from '~/hooks/useOrderPaymentGate';

// Router
import { useNavigate } from 'react-router-dom';

// Utils
import { MESSAGES } from '~/utils/message';
import { numberWithComma, trimSpaces } from '~/utils/helperFunctions';
import { DEFAULT_PAGINATION, HAS_ENVELOPE } from '~/utils/constants';
import { isPaymentMethodRequiredResponse, isPaymentMethodStillMissing } from '~/utils/payment-gate';
import { getImpersonationMode } from '~/utils/auth';
import { addDays } from 'date-fns';

// Components
import AddContacts from './AddContacts';
import PaymentMethodRequiredModal from '~/components/Orders/PaymentMethodRequiredModal';
import InsufficientFundsWarningModal from '~/components/Orders/InsufficientFundsWarningModal';
import MailerDetails from './MailerDetails';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';

// MUI Components
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  GlobalStyles,
  CircularProgress,
} from '@mui/material';

// Images
import { WarningIcon } from '~/assets/images';
import OrderDetails, { addBusinessDays } from './OrderDetails';
import ReviewOrder from './ReviewOrder';
import CampaignCostBreakdown from './ReviewOrder/CampaignsCostBreakDownModal';
import { balanceFormatter } from '~/utils/helperFunctions';
import { CONTACTS_OPTION_TYPE } from '~/utils/constants';
import { getItem, removeItem } from '~/utils/local-storage';
import { failure, success } from '~/redux/actions/snackbar-actions';
import SenderProfileModal from '~/components/Company/CompanyProfile/SenderProfiles/SenderProfileModal';
import OlcButton from '~/components/General/OlcButton';

const steps = ['Add Contacts', 'Mailer Details', 'Order Details', 'Review Order'];

const CustomStepIcon = (props) => {
  const { className } = props;
  return (
    <Box className={className}>
      <Typography>{props.icon}</Typography>
    </Box>
  );
};

const OrderFlow = ({
  onCancel,
  onBack,
  view,
  initialStep = 0,
  initialReqId = null,
  preSelectedContactIds = [],
}) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [isUploadListFileSelected, setIsUploadListFileSelected] = useState(false);
  const [processFileSignal, setProcessFileSignal] = useState(0);
  const [importSubStep, setImportSubStep] = useState(0); // 0: upload, 1: map, 2: progress
  const [progressCanImport, setProgressCanImport] = useState(false);
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressProcessing, setProgressProcessing] = useState(false);
  const [triggerProgressImportSignal, setTriggerProgressImportSignal] = useState(0);
  const [triggerProgressBackSignal, setTriggerProgressBackSignal] = useState(0);
  const [parentBackSignal, setParentBackSignal] = useState(0);
  const [footerErrorMessage, setFooterErrorMessage] = useState('');
  const [isMailerDetailsValid, setIsMailerDetailsValid] = useState(false);
  const [hasContactsSelected, setHasContactsSelected] = useState(false);
  const [addContactsTotal, setAddContactsTotal] = useState(0);

  // Persist selected contacts state across step navigation
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedImportLists, setSelectedImportLists] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsContactCounts, setTagsContactCounts] = useState({});
  // Persist caches for AddContacts so selected labels remain correct across steps
  const [selectedContactsCache, setSelectedContactsCache] = useState({});
  const [selectedTagsCache, setSelectedTagsCache] = useState({});
  const [selectedImportListsCache, setSelectedImportListsCache] = useState({});
  const [importSelectedFile, setImportSelectedFile] = useState([]);
  const [importTagsData, setImportTagsData] = useState({
    selectedTags: [],
    selectedTagIds: [],
    isDncList: false,
  });

  // Persist mailer details state across step navigation
  const [orderType, setOrderType] = useState('single-mailer');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTab, setPreviewTab] = useState('mailer');
  const [selectedPostage, setSelectedPostage] = useState(null);
  const [selectedEnvelope, setSelectedEnvelope] = useState(null);
  const [offerPercentageValue, setOfferPercentageValue] = useState('');
  const [uvCoating, setUvCoating] = useState(false);
  const [templatePreview, setTemplatePreview] = useState({
    templateUrl: null,
    backTemplateUrl: null,
  });
  const [isBackSide, setIsBackSide] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [envelopeImage, setEnvelopeImage] = useState(null);
  // OrderDetails-related state (ported from old flow)
  const [selectedReturnedAddress, setSelectedReturnedAddress] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [expectedDateData, setExpectedDateData] = useState(null);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(addBusinessDays(new Date(), 2));
  const [search, setSearch] = useState('');
  // Campaign search for MailerDetails (separate from return address search)
  const [campaignSearch, setCampaignSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    returnAddress: false,
    date: false,
    review: false,
  });

  const [orderName, setOrderName] = useState('');
  // Batch settings (only relevant for multiple contacts / upload list)
  const [numberOfBatches, setNumberOfBatches] = useState(1);
  const [daysBetweenBatches, setDaysBetweenBatches] = useState('');
  // Selection type and selected campaign (wired from Mailer Details)
  const [selectionType, setSelectionType] = useState({ id: 1, label: 'Single Mailer' });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const datepickerRef = useRef(null);
  const prevFileRef = useRef(null);
  const orderFlowContentRef = useRef(null);

  // Review step state parity with old flow
  const [isFlipped, setFlipped] = useState(false);
  const [tabValue, setTabValue] = useState({ type: 'mail_proof' });
  const [checkedIds, setCheckedIds] = useState([]);
  const [downloadingRows, setDownloadingRows] = useState([]);
  const [orderProofDownloaded, setOrderProofDownloaded] = useState(false);
  const [envelopeProofDownloaded, setEnvelopeProofDownloaded] = useState(false);
  // Cost details and breakdown modal
  const [costDetails, setCostDetails] = useState(null);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [orderSubmitLoader, setOrderSubmitLoader] = useState(false);
  const [isCostValidating, setIsCostValidating] = useState(false);
  const [isOrderDetailsValid, setIsOrderDetailsValid] = useState(false);
  const [isReviewValid, setIsReviewValid] = useState(false);
  const [isSenderProfileModalOpen, setIsSenderProfileModalOpen] = useState(false);

  // Contacts context derived from selections (mirror old flow)
  const [contactType, setContactType] = useState('Single'); // 'Single' | 'SingleArray' | 'Multiple' | 'Upload'
  const [selectedContact, setSelectedContact] = useState(null); // { id: '...' } or reqId
  const [deliverable, setDeliverable] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentGate = useOrderPaymentGate();
  // An admin impersonating a customer on ACH can place the order without a card
  // by confirming the customer's wallet is (or will be) funded.
  const isImpersonating = !!getImpersonationMode();
  const [achFundsConfirmed, setAchFundsConfirmed] = useState(false);
  const { closeGate } = paymentGate;
  // Dismissing the modal withdraws the ACH confirmation so it can't linger unseen.
  const closePaymentGate = useCallback(() => {
    closeGate();
    setAchFundsConfirmed(false);
  }, [closeGate]);
  const [lowBalanceWarning, setLowBalanceWarning] = useState(null); // { balance, cost }

  const handleOrderTypeChange = (nextOrderType) => {
    if (nextOrderType !== orderType) {
      setNumberOfBatches(1);
      setDaysBetweenBatches('');
    }

    setOrderType(nextOrderType);
  };

  useEffect(() => {
    setActiveStep(initialStep);
  }, [initialStep]);

  // The scroll container is shared by every step. Reset it before paint so a
  // step never inherits the previous step's scroll position for one frame.
  useLayoutEffect(() => {
    if (!orderFlowContentRef.current) return;

    orderFlowContentRef.current.scrollTop = 0;
    orderFlowContentRef.current.scrollLeft = 0;
  }, [activeStep]);

  // Scheduled mail feature flag (same logic as old flow)
  const isScheduledMail = useSelector(
    (state) =>
      state?.userReducers?.user?.subscribedPlan?.meta?.stripeMeta?.metadata?.general_scheduled_mail
  );
  // Batching feature flag — reads from the flat features array returned by auth/me
  const batchingAllowedFeature = useSelector((state) =>
    state?.userReducers?.user?.features?.find((f) => f.slug === 'batching_allowed')
  );
  const products = useSelector((state) => state.productReducers.products);
  const isScheduledMailDisabled = () => {
    if (!isScheduledMail) return true;
    try {
      const parsed = JSON.parse(isScheduledMail);
      return parsed?.limit == 0;
    } catch (e) {
      return true;
    }
  };
  // Defaults to true (fail open) so existing users are never blocked if flag is missing
  const isBatchingAllowed = () => {
    if (!batchingAllowedFeature) return true;
    return batchingAllowedFeature.value === true;
  };

  const preSelectedContactIdsRef = useRef(preSelectedContactIds);
  const initialReqIdRef = useRef(initialReqId);

  useEffect(() => {
    const contacts = preSelectedContactIdsRef.current;
    if (!contacts.length) return;

    const validIds = contacts
      .map((c) => c.id)
      .filter((id) => Number.isFinite(Number(id)) && Number(id) > 0);

    if (!validIds.length) return;

    setSelectedContacts(validIds);

    // Cache contact objects so MultiSelect chips render correctly across step navigation.
    const cache = {};
    contacts.forEach((c) => {
      if (c.id) cache[String(c.id)] = { ...c };
    });
    setSelectedContactsCache(cache);
  }, []);

  // Reset Review step state whenever the user changes template or campaign in Mailer Details
  useEffect(() => {
    // Clear review-specific validation and selections
    setCheckedIds([]);
    setDownloadingRows([]);
    setOrderProofDownloaded(false);
    setEnvelopeProofDownloaded(false);
    setFlipped(false);
    setTabValue({ type: 'mail_proof' });
    setIsReviewValid(false);
    setErrors((prev) => ({
      ...prev,
      review: false,
    }));
  }, [selectedTemplate, selectedCampaign]);

  const getBackButtonLabel = () => {
    if (activeStep === 0) {
      if (view === 'upload_list') {
        if (importSubStep === 2) return 'Back to Mapping';
        if (importSubStep === 1) return 'Back to Uploading';
        return 'Back to Create New Order';
      }
      return 'Back to Create New Order';
    }
    const prevLabel = steps[Math.max(activeStep - 1, 0)] || 'Previous';
    return `Back to ${prevLabel}`;
  };

  // Return address options (same mapping as old flow)
  const returnOptions =
    useSelector((state) => state.contactsReducers.returnAddresses)
      ?.map((contact) => {
        const profilePrefix = contact.profileName ? `${contact.profileName} - ` : '';
        const label = `${profilePrefix}${contact.fullName}, ${contact.address1} ${contact?.address2 || ''} ${contact?.city || ''}, ${contact?.state || ''} ${contact?.zip || ''}`;
        return {
          id: contact.id,
          label,
          title: label,
          ...contact,
        };
      })
      .concat(
        {
          id: '-1',
          label: '-No Return Address-',
          title: '-No Return Address-',
          custom: true,
          hideDividerAbove: true,
        },
        {
          id: '-2',
          label: 'Create New Sender Profile',
          title: 'Create New Sender Profile',
          custom: true,
          func: () => setIsSenderProfileModalOpen(true),
        }
      ) || [];

  // Schedule options (same as old flow)
  const scheduleOptions = [
    {
      id: 1,
      label: '-ASAP-',
      value: 'ASAP',
      custom: true,
    },
    {
      id: 2,
      label: '-Select Date-',
      func: () => setIsDatepickerOpen(true),
      custom: false,
      customPlaceholder: isScheduledMailDisabled(),
    },
  ];

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevious = () => {
    setActiveStep((prev) => {
      const newStep = Math.max(prev - 1, 0);
      // Reset validation when leaving step 1
      if (prev === 1) {
        setIsMailerDetailsValid(false);
      }
      return newStep;
    });
  };

  // Reset validation when step changes
  useEffect(() => {
    if (activeStep !== 1) {
      setIsMailerDetailsValid(false);
    }
  }, [activeStep]);

  // Load templates similar to the old flow
  useEffect(() => {
    dispatch(getAllTemplates(DEFAULT_PAGINATION.page, DEFAULT_PAGINATION.pageSize));
  }, []);

  // Pre-fetch ASAP expected date immediately when component mounts
  // This ensures the date is ready before user reaches step 2, preventing label delay
  useEffect(() => {
    const fetchAsapExpectedDate = async () => {
      const response = await dispatch(
        fetchExpectedDeliveryDate({
          mailDate: 'ASAP',
        })
      );
      if (response?.status === 200) {
        setExpectedDateData(response.data.data);
      }
    };
    // Fetch ASAP date on mount so it's ready when user reaches step 2
    if (!expectedDateData) {
      fetchAsapExpectedDate();
    }
  }, [dispatch]);

  // Close datepicker on outside click (same behavior as old flow)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (datepickerRef.current &&
          !event.target.contains?.(datepickerRef.current) &&
          !event.target.closest?.('.react-datepicker') &&
          datepickerRef.current) ||
        datepickerRef.current === event.target
      ) {
        setIsDatepickerOpen(false);
      }
    };
    if (isDatepickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatepickerOpen]);

  // Fetch return addresses when entering Order Details step
  useEffect(() => {
    if (activeStep === 2 && !selectedReturnedAddress) {
      dispatch(
        fetchReturnAddresses({
          ...DEFAULT_PAGINATION,
          isReturnAddress: true,
          excludeDncList: true,
        })
      );
    }
  }, [activeStep]);

  // Search handler to fetch return addresses (mirrors old flow behavior for this step)
  useEffect(() => {
    const runSearch = async () => {
      if (activeStep !== 2) return;
      setLoading(true);
      await dispatch(
        fetchReturnAddresses({
          ...DEFAULT_PAGINATION,
          search: search?.replace(/^(\S+\s+\S+)\s*(.*)/, '$1').trim(),
          isReturnAddress: true,
          excludeDncList: true,
        })
      );
      setLoading(false);
    };
    if (search !== undefined) {
      runSearch();
    }
  }, [search, activeStep]);

  // Compute expected delivery date when date selected and on relevant steps
  useEffect(() => {
    const getExpectedDeliveryDate = async () => {
      const hasBatches = numberOfBatches > 1 && Number(daysBetweenBatches) > 0;

      // Single-mailer without batches: compute locally to avoid unnecessary API call
      if (orderType === 'single-mailer' && !hasBatches) {
        if (selectedDate?.id === 1 || selectedDate?.value === 'ASAP') {
          const tomorrow = addBusinessDays(new Date(), 2);

          const formattedTomorrow = tomorrow
            .toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
            .replace(',', '');

          setExpectedDateData({ mailedDate: formattedTomorrow });
        } else {
          const formattedSelectedDate = new Date(selectedDate)
            .toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
            .replace(',', '');

          setExpectedDateData({ mailedDate: formattedSelectedDate });
        }
        return;
      }

      const batchParams = hasBatches
        ? { numberOfBatches, daysBetweenEachBatch: Number(daysBetweenBatches) }
        : {};

      const payload = {
        mailingDate: typeof selectedDate === 'object' ? selectedDate?.value : selectedDate,
        ...(selectionType?.id === 2 && selectedCampaign?.campaignId
          ? { automationId: selectedCampaign.campaignId }
          : { templateId: selectedTemplate?.id }),
        ...batchParams,
      };
      const response = await dispatch(fetchExpectedDeliveryDate(payload));
      if (response?.status === 200) {
        setExpectedDateData(response.data.data);
      } else {
        setExpectedDateData(null);
      }
    };
    if (activeStep === 2) {
      if (selectedDate) {
        getExpectedDeliveryDate();
      }
      // If no selectedDate on Order Details, keep existing expectedDateData (may come from ASAP fetch)
    } else if (activeStep !== 1 && activeStep !== 3) {
      // Clear expectedDateData only when leaving steps 1/2/3 entirely (e.g. back to step 0)
      // Step 3 (Review) reads expectedDateData from state — don't wipe it on entry
      setExpectedDateData(null);
    }
  }, [
    selectedDate,
    activeStep,
    dispatch,
    selectionType?.id,
    selectedCampaign?.campaignId,
    selectedTemplate?.id,
    numberOfBatches,
    daysBetweenBatches,
    orderType,
  ]);

  // Pre-fetch ASAP expected date when entering step 1 so it's ready for step 2
  // This ensures the date is available immediately when step 2 renders, preventing label delay
  useEffect(() => {
    const fetchAsapExpectedDate = async () => {
      const response = await dispatch(
        fetchExpectedDeliveryDate({
          scheduledDate: 'ASAP',
        })
      );
      if (response?.status === 200) {
        setExpectedDateData(response.data.data);
      }
    };
    // Fetch ASAP date early (when entering step 1) so it's ready for step 2 dropdown
    // This prevents the "ASAP" label from appearing first and then updating with the date
    if ((activeStep === 1 || activeStep === 2) && !selectedDate && !expectedDateData) {
      fetchAsapExpectedDate();
    }
  }, [activeStep, selectedDate, dispatch]);

  // Clear old file data when a new file is selected in upload_list view
  useEffect(() => {
    if (view !== 'upload_list') return;

    const currentFile = importSelectedFile?.[0];
    const prevFile = prevFileRef.current;

    // Only process if we have a file to compare
    if (!currentFile && !prevFile) {
      prevFileRef.current = currentFile;
      return;
    }

    // Get file identifiers for comparison (name and size to be more accurate)
    const currentFileId = currentFile ? `${currentFile.name}-${currentFile.size}` : null;
    const prevFileId = prevFile ? `${prevFile.name}-${prevFile.size}` : null;

    // Detect if a new file is selected (different from previous) - only clear if there was a previous file
    const fileChanged = prevFile && currentFileId !== prevFileId;

    if (fileChanged) {
      // Clear old data when a new file is selected (only if there was a previous file)
      // Clear reqId from localStorage - it will be set again when the new file is processed
      removeItem('reqId');
      setProgressTotal(0);
      setImportTagsData({
        selectedTags: [],
        selectedTagIds: [],
        isDncList: false,
      });
      setProgressCanImport(false);
      setProgressProcessing(false);
      setFooterErrorMessage('');
      // selectedContact will be updated by the useEffect that derives it from reqId
      // Since reqId is cleared, selectedContact will be set to null
    }

    // Update ref to track current file
    prevFileRef.current = currentFile;
  }, [view, importSelectedFile]);

  // Derive contactType and selectedContact from Add Contacts selections (parity with old flow)
  useEffect(() => {
    if (view === 'add_contacts') {
      if ((selectedTags || []).length > 0) {
        setContactType('Multiple');
        setSelectedContact({ id: selectedTags[0] });
      } else if ((selectedContacts || []).length > 0) {
        if (selectedContacts.length === 1) {
          setContactType('Single');
          setSelectedContact({ id: selectedContacts[0] });
        } else {
          setContactType('SingleArray');
          setSelectedContact({ id: selectedContacts.join(',') });
        }
      } else {
        setContactType('Single');
        setSelectedContact(null);
      }
      return;
    }

    if (view === 'upload_list') {
      setContactType('Upload');
      const reqId = initialReqIdRef.current || getItem('reqId');
      setSelectedContact(reqId || null);
      return;
    }
  }, [view, selectedContacts, selectedTags, importSelectedFile, progressTotal]);

  // Estimated cost shown on the Review step and used for the wallet funds check.
  const estimatedCost = useMemo(() => {
    if (selectionType?.id === 2) {
      // Campaigns: sum breakdown if available
      return (costDetails?.breakdown || [])
        .filter((item) => item.cost)
        .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
    }
    // Single mailer with batches: cost is a string on the root data object.
    // Single mailer without batches: costDetails is the cost object.
    return parseFloat(costDetails?.cost || 0) || 0;
  }, [selectionType?.id, costDetails]);

  // Submit Order (old flow parity @index.jsx 558-609)
  const submitOrder = async ({ skipFundsCheck = false } = {}) => {
    try {
      // Use computed deliverable (set from cost API or selections) like old flow behavior
      if ((deliverable || 0) <= 0) {
        return dispatch(failure(MESSAGES.ORDERS.CREATE.DELIVERABLES_REQUIRED));
      }
      // Live-mode orders need a card on file. Keep the prepared order and ask for one first.
      if (paymentGate.isCardMissing) {
        // Only an impersonating admin who confirmed the customer is on ACH may skip the card.
        if (!(isImpersonating && achFundsConfirmed)) {
          // The cached account may predate a card that was just saved, so re-check with fresh
          // data before blocking. The loader guards against a double click while we wait.
          setOrderSubmitLoader(true);
          if (await isPaymentMethodStillMissing(() => dispatch(getUserData()))) {
            return paymentGate.openGate();
          }
        } else if (!skipFundsCheck) {
          // Warn (never block) when the customer's wallet does not cover the order.
          const userResponse = await dispatch(getUserData());
          const balance = Number(userResponse?.data?.data?.organization?.balance ?? 0) || 0;
          if (balance < estimatedCost) {
            return setLowBalanceWarning({ balance, cost: estimatedCost });
          }
        }
      }
      setOrderSubmitLoader(true);
      const key = CONTACTS_OPTION_TYPE[contactType === 'Single' ? 'SingleArray' : contactType];
      const payloadKey = key === 'tag' ? 'tagIds' : key;
      let payload;
      if (selectionType && selectionType.id === 2) {
        payload = {
          automationId: selectedCampaign?.campaignId,
        };
      } else {
        const productIdExact =
          products.find(
            (product) =>
              product.productType === selectedTemplate?.object?.product?.productType &&
              product.deliveryType ===
                (selectedPostage?.value ||
                  selectedTemplate?.object?.product?.deliveryType ||
                  selectedTemplate?.object?.product?.postageType) &&
              product.paperSize === selectedTemplate?.object?.product?.paperSize &&
              (selectedEnvelope ? product.envelopeType === selectedEnvelope.label : true)
          )?.id || selectedTemplate?.object?.product?.id;

        const productIdFallback =
          productIdExact ||
          selectedTemplate?.productId ||
          selectedTemplate?.object?.product?.id ||
          selectedTemplate?.object?.productId ||
          (products || []).find((p) => p.productType === selectedTemplate?.productType)?.id ||
          null;

        payload = {
          productId: productIdFallback,
          templateId: selectedTemplate?.id,
        };
      }
      payload.mailingDate = typeof selectedDate === 'object' ? selectedDate?.value : selectedDate;
      if (isImpersonating && achFundsConfirmed && paymentGate.isCardMissing) {
        payload.adminConfirmedAchFunding = true;
      }

      // Build contact-related payload value based on key type
      const contactValueForSubmit =
        key === 'reqId'
          ? // For upload_list, reqId may live in state or localStorage
            selectedContact || getItem('reqId')
          : key === 'contactIds'
            ? String(selectedContact?.id || '')
                .split(',')
                .map((id) => (id ? +id : null))
                .filter((id) => Number.isFinite(id))
            : key === 'tag'
              ? // When using tags (Multiple selection), send all selected tag ids
                selectedTags || []
              : selectedContact?.id;

      ((payload.name = trimSpaces(orderName || '')),
        (payload.rosOfferPercentage = offerPercentageValue),
        (payload.uvCoating = uvCoating),
        (payload.errorRecordCount = 0),
        (payload.returnAddress =
          selectedReturnedAddress?.id === '-1' ? null : selectedReturnedAddress?.id),
        (payload[payloadKey] = contactValueForSubmit));

      if (numberOfBatches > 1 && Number(daysBetweenBatches) > 0) {
        payload.numberOfBatches = numberOfBatches;
        payload.daysBetweenEachBatch = Number(daysBetweenBatches);
      }

      const response = await dispatch(creatOrder(payload));
      if (response?.status === 200) {
        dispatch(success('Order submitted successfully'));
        navigate('/orders');
      } else if (isPaymentMethodRequiredResponse(response)) {
        // The server is the source of truth: our cached account data was stale. Resync it and
        // ask for a card instead of showing a raw error.
        dispatch(getUserData());
        // If an ACH confirmation was sent and still rejected, fall back to asking for a card.
        setAchFundsConfirmed(false);
        paymentGate.openGate();
      } else {
        dispatch(failure(response?.data?.message));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setOrderSubmitLoader(false);
    }
  };

  // Build the calculate-cost API payload from current state.
  // Returns null when required fields are missing (caller should abort).
  const buildCostPayload = () => {
    const key = CONTACTS_OPTION_TYPE[contactType];
    const payloadKey = key === 'tag' ? 'tagIds' : key;

    const batchParams =
      numberOfBatches > 1 && Number(daysBetweenBatches) > 0
        ? { numberOfBatches, daysBetweenEachBatch: Number(daysBetweenBatches) }
        : {};

    if (selectionType?.id === 2) {
      if (!selectedCampaign?.campaignId || !key) return null;
      if (key === 'reqId') {
        const reqId = selectedContact || getItem('reqId');
        if (!reqId) return null;
      } else if (key !== 'tag' && !selectedContact) {
        return null;
      }
      const contactValue =
        key === 'reqId'
          ? selectedContact || getItem('reqId')
          : key === 'contactIds'
            ? String(selectedContact?.id || '')
                .split(',')
                .map((id) => (id ? +id : null))
                .filter(Number.isFinite)
            : key === 'tag'
              ? selectedTags || []
              : selectedContact?.id;
      return {
        automationId: selectedCampaign?.campaignId,
        [payloadKey]: contactValue,
        ...batchParams,
      };
    }

    if (!selectedTemplate?.id) return null;

    const productIdExact =
      products.find(
        (product) =>
          product.productType === selectedTemplate?.object?.product?.productType &&
          product.deliveryType ===
            (selectedPostage?.value ||
              selectedTemplate?.object?.product?.deliveryType ||
              selectedTemplate?.object?.product?.postageType) &&
          product.paperSize === selectedTemplate?.object?.product?.paperSize &&
          (selectedEnvelope ? product.envelopeType === selectedEnvelope.label : true)
      )?.id || selectedTemplate?.object?.product?.id;

    const productId =
      productIdExact ||
      selectedTemplate?.productId ||
      selectedTemplate?.object?.product?.id ||
      selectedTemplate?.object?.productId ||
      (products || []).find((p) => p.productType === selectedTemplate?.productType)?.id ||
      null;

    if (!key) return null;
    if (key === 'reqId') {
      const reqId = selectedContact || getItem('reqId');
      if (!reqId) return null;
    } else if (key !== 'tag' && !selectedContact) {
      return null;
    }

    const contactValue =
      key === 'reqId'
        ? selectedContact || getItem('reqId')
        : key === 'contactIds'
          ? String(selectedContact?.id || '')
              .split(',')
              .map((id) => (id ? +id : null))
              .filter(Number.isFinite)
          : key === 'tag'
            ? selectedTags || []
            : selectedContact?.id;

    return {
      templateId: selectedTemplate?.id,
      productId,
      [payloadKey]: contactValue,
      ...batchParams,
      ...(uvCoating ? { uvCoating: true } : {}),
    };
  };

  // Apply the cost API response to state — shared between pre-nav validation and Review refresh.
  const applyCostResponse = (data) => {
    if (selectionType?.id === 2) {
      setCostDetails(data);
      setDeliverable(data?.deliverableContactsCount || 0);
    } else if (numberOfBatches > 1) {
      setCostDetails(data);
      setDeliverable(data?.deliverableContactsCount || 0);
    } else {
      setCostDetails(data?.cost);
      setDeliverable(data?.cost?.deliverableContacts || 0);
    }
  };

  // Called when the user clicks Next on the Order Details step (step 2).
  // Validates cost before navigating — shows a snackbar on error, proceeds on success.
  const handleOrderDetailsNext = async () => {
    const payload = buildCostPayload();
    if (!payload) return;

    try {
      setIsCostValidating(true);
      const response = await dispatch(fetchOrderCost(payload));

      if (response?.status === 200) {
        applyCostResponse(response.data.data);
        handleNext();
      } else {
        const message =
          response?.data?.message ||
          response?.data?.data?.message ||
          'Failed to calculate cost. Please try again.';
        dispatch(failure(message));
      }
    } catch {
      dispatch(failure('Failed to calculate cost. Please try again.'));
    } finally {
      setIsCostValidating(false);
    }
  };

  // Reset deliverable to 0 only while the user is on step 0 (Add Contacts / Upload List).
  // On every subsequent step the cost API sets the authoritative deliverable count via
  // applyCostResponse, so we must not override it here.
  useEffect(() => {
    if (activeStep !== 0) return;
    if (view === 'add_contacts') {
      setDeliverable(0);
    } else if (view === 'upload_list') {
      setDeliverable(Number(progressTotal || 0));
    }
  }, [view, activeStep, progressTotal]);

  // Batch UI is only shown for multiple contacts or uploaded lists (not single contact)
  const isMultipleContacts = contactType !== 'Single';

  // Normalize single-mailer cost details to reuse campaign breakdown UI
  const singleMailerBreakdown = useMemo(() => {
    if (!costDetails || selectionType?.id !== 1 || !selectedTemplate) return null;

    // Derive a clean template title from label like "ID:123 Title"
    const templateTitle =
      (selectedTemplate?.label || '').replace(/^ID:\s*\d+\s*/i, '') ||
      selectedTemplate?.label ||
      '';

    const perMailerCost =
      costDetails?.perMailerCost ?? costDetails?.breakdown?.[0]?.perMailerCost ?? 0;
    const perMailerGsvCost = costDetails?.perMailerGsvCost ?? 0;
    const perMailerRosCost = costDetails?.perMailerRosCost ?? 0;
    const perMailerAdditionalPageCost = costDetails?.perMailerAdditionalPageCost ?? 0;
    const perMailerUvCoatingCost = costDetails?.perMailerUvCoatingCost ?? 0;

    const row = {
      id: selectedTemplate?.id || 1,
      templateId: selectedTemplate?.id,
      template: { title: templateTitle },
      product: {
        id: selectedTemplate?.productId,
        productType: selectedTemplate?.productType,
      },
      deliverableContacts: deliverable || 0,
      perMailerCost: perMailerCost,
      hasGsv: !!costDetails?.costGsv,
      hasRos: !!costDetails?.costRos,
      hasAdditionalPage: !!costDetails?.hasAdditionalPage || !!costDetails?.costAdditionalPage,
      hasUvCoating: !!costDetails?.costUvCoating,
      costGsv: costDetails?.costGsv || 0,
      costRos: costDetails?.costRos || 0,
      costUvCoating: costDetails?.costUvCoating || 0,
      costAdditionalPage: costDetails?.costAdditionalPage || 0,
      perMailerGsvCost,
      perMailerRosCost,
      perMailerAdditionalPageCost,
      perMailerUvCoatingCost,
      cost: costDetails?.cost ?? 0,
      additionalPage: costDetails?.costAdditionalPage || 0,
      perMaillerAdditionalPage: perMailerAdditionalPageCost,
      type: 'single',
    };

    return { breakdown: [row] };
  }, [costDetails, selectionType?.id, selectedTemplate, deliverable]);

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Container maxWidth="xxl" className="addContactsContainer">
        <Box
          className={`addContactsCard ${
            activeStep === steps.length - 1 ? 'addContactsCard--review' : ''
          }`}
        >
          <Box className="addContactsHeader">
            <Typography className="pageTitle">{MESSAGES?.ORDERS?.CREATE?.TITLE}</Typography>

            <Stepper activeStep={activeStep} className="importWizardStepper">
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    className={activeStep === index ? 'active' : ''}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box ref={orderFlowContentRef} className="orderFlowContent">
            {activeStep === 0 && (
              <AddContacts
                view={view}
                onFileSelectedChange={setIsUploadListFileSelected}
                processFileSignal={processFileSignal}
                onSubStepChange={setImportSubStep}
                onProgressStateChange={({
                  canImport,
                  total,
                  isProcessing,
                  errorMessage,
                  importCompleted,
                }) => {
                  setProgressCanImport(!!canImport);
                  setProgressTotal(total || 0);
                  setProgressProcessing(!!isProcessing);
                  if (typeof errorMessage === 'string') {
                    setFooterErrorMessage(errorMessage);
                  } else if (isProcessing || canImport) {
                    setFooterErrorMessage('');
                  }
                  if (importCompleted) {
                    setActiveStep(1);
                  }
                }}
                triggerProgressImportSignal={triggerProgressImportSignal}
                triggerProgressBackSignal={triggerProgressBackSignal}
                parentBackSignal={parentBackSignal}
                onContactsSelectionChange={setHasContactsSelected}
                onContactsCountChange={setAddContactsTotal}
                // Pass persisted state and setters
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
                selectedImportLists={selectedImportLists}
                setSelectedImportLists={setSelectedImportLists}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                tagsContactCounts={tagsContactCounts}
                setTagsContactCounts={setTagsContactCounts}
                importSelectedFile={importSelectedFile}
                setImportSelectedFile={setImportSelectedFile}
                importTagsData={importTagsData}
                setImportTagsData={setImportTagsData}
                selectedContactsCache={selectedContactsCache}
                setSelectedContactsCache={setSelectedContactsCache}
                selectedTagsCache={selectedTagsCache}
                setSelectedTagsCache={setSelectedTagsCache}
                selectedImportListsCache={selectedImportListsCache}
                setSelectedImportListsCache={setSelectedImportListsCache}
                preSelectedContactIds={preSelectedContactIdsRef.current}
              />
            )}
            {activeStep === 1 && (
              <MailerDetails
                onValidationChange={setIsMailerDetailsValid}
                // Pass persisted state and setters
                orderType={orderType}
                setOrderType={handleOrderTypeChange}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                previewTab={previewTab}
                setPreviewTab={setPreviewTab}
                selectedPostage={selectedPostage}
                setSelectedPostage={setSelectedPostage}
                selectedEnvelope={selectedEnvelope}
                setSelectedEnvelope={setSelectedEnvelope}
                offerPercentageValue={offerPercentageValue}
                setOfferPercentageValue={setOfferPercentageValue}
                uvCoating={uvCoating}
                setUvCoating={setUvCoating}
                templatePreview={templatePreview}
                setTemplatePreview={setTemplatePreview}
                isBackSide={isBackSide}
                setIsBackSide={setIsBackSide}
                imageLoading={imageLoading}
                setImageLoading={setImageLoading}
                envelopeImage={envelopeImage}
                setEnvelopeImage={setEnvelopeImage}
                // Selection type and campaign wiring
                onSelectionTypeChange={setSelectionType}
                onCampaignChange={setSelectedCampaign}
                onTemplateChange={setSelectedTemplate}
                onPostageChange={setSelectedPostage}
                onEnvelopeChange={setSelectedEnvelope}
                onEnvelopeImageChange={setEnvelopeImage}
                selectedCampaign={selectedCampaign}
                setSelectedCampaign={setSelectedCampaign}
                campaignSearch={campaignSearch}
                setCampaignSearch={setCampaignSearch}
              />
            )}
            {activeStep === 2 && (
              <Box className="orderDetailsWapper">
                <OrderDetails
                  returnOptions={returnOptions}
                  selectedReturnedAddress={selectedReturnedAddress}
                  setSelectedReturnedAddress={setSelectedReturnedAddress}
                  scheduleOptions={scheduleOptions}
                  selectedDate={selectedDate}
                  expectedDateData={expectedDateData}
                  setSelectedDate={setSelectedDate}
                  isDatepickerOpen={isDatepickerOpen}
                  datepickerRef={datepickerRef}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  setIsDatepickerOpen={setIsDatepickerOpen}
                  setSearch={setSearch}
                  setErrors={setErrors}
                  loading={loading}
                  errors={errors}
                  orderName={orderName}
                  setOrderName={setOrderName}
                  selectionType={selectionType}
                  selectedCampaign={selectedCampaign}
                  selectedTemplate={selectedTemplate}
                  onValidationChange={setIsOrderDetailsValid}
                  numberOfBatches={numberOfBatches}
                  setNumberOfBatches={setNumberOfBatches}
                  daysBetweenBatches={daysBetweenBatches}
                  setDaysBetweenBatches={setDaysBetweenBatches}
                  isMultipleContacts={isMultipleContacts}
                  isBatchingAllowed={isBatchingAllowed()}
                />
              </Box>
            )}
            {activeStep === 3 && (
              <Box
                className={`reviewOrderStep ${
                  selectionType?.id === 2 ? 'reviewOrderStep--campaign' : ''
                }`.trim()}
              >
                <ReviewOrder
                  forntImage={selectedTemplate?.frontThumbnailUrl}
                  envelopeImage={envelopeImage || selectedTemplate?.envelopeThumbnailUrl}
                  template={selectedTemplate}
                  selectedReturnedAddress={selectedReturnedAddress}
                  contactType={contactType}
                  selectedContact={selectedContact}
                  deliverable={deliverable}
                  updateErrors={setErrors}
                  errors={errors.review}
                  name={'review'}
                  checkedIds={checkedIds}
                  setCheckedIds={setCheckedIds}
                  isTemplateAvailable={!!selectedTemplate}
                  setOrderProofDownloaded={setOrderProofDownloaded}
                  setEnvelopeProofDownloaded={setEnvelopeProofDownloaded}
                  products={products}
                  selectedEnvelope={selectedEnvelope}
                  selectedPostage={selectedPostage}
                  selectedTemplate={selectedTemplate}
                  selectionType={selectionType}
                  selectedCampaign={selectedCampaign}
                  isFlipped={isFlipped}
                  setFlipped={setFlipped}
                  handleTabChange={(_, newValue) => setTabValue({ type: newValue })}
                  tabValue={tabValue}
                  setTabValue={setTabValue}
                  downloadingRows={downloadingRows}
                  setDownloadingRows={setDownloadingRows}
                  activeItemId={4}
                  expectedDateData={expectedDateData}
                  selectedDate={selectedDate}
                  onValidationChange={setIsReviewValid}
                />
              </Box>
            )}
          </Box>

          <Box
            className={`footerActions footerActions--step-${activeStep + 1} ${
              activeStep === steps.length - 1 ? 'footerActions--review' : ''
            }`}
          >
            <OlcPageBackButton
              className="backLink backBtn"
              onClick={() => {
                if (activeStep === 0) {
                  if (view === 'upload_list') {
                    if (importSubStep === 2) {
                      setTriggerProgressBackSignal((p) => p + 1); // Back to Mapping
                    } else if (importSubStep === 1) {
                      setParentBackSignal((p) => p + 1); // Back to Uploading
                    } else {
                      // First step and first sub-step: go back to main Create Order screen
                      if (typeof onBack === 'function') {
                        onBack();
                      } else if (typeof onCancel === 'function') {
                        onCancel();
                      }
                    }
                  } else {
                    // First step for other flows: go back to main Create Order screen
                    if (typeof onBack === 'function') {
                      onBack();
                    } else if (typeof onCancel === 'function') {
                      onCancel();
                    }
                  }
                } else {
                  handlePrevious();
                }
              }}
              disabled={false}
            >
              {getBackButtonLabel()}
            </OlcPageBackButton>

            {/* Estimated Cost + View Cost Breakdown (only on Review step) */}
            {activeStep === steps.length - 1 && (
              <Box
                className="totalContactsFooter totalContactsFooter--estimatedCost"
                sx={{ mx: 2, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Typography>
                  Estimated Cost{' '}
                  <span className="boldNum costNum">
                    {balanceFormatter(estimatedCost)}
                  </span>
                </Typography>
                {/* {selectionType?.id === 2 && (costDetails?.breakdown || []).length > 0 && ( */}
                {true && (
                  <Typography
                    className="costBreakdownLink"
                    variant="text"
                    sx={{
                      color: '#ed5c2f',
                      textTransform: 'none',
                      textDecoration: 'underline',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowCostBreakdown(true)}
                  >
                    View Cost Breakdown
                  </Typography>
                )}
              </Box>
            )}
            {(view === 'add_contacts' && activeStep === 0) ||
            (view === 'upload_list' &&
              activeStep === 0 &&
              importSubStep === 2 &&
              !progressProcessing) ? (
              <Box className="totalContactsFooter totalContactsFooter--contacts" sx={{ mx: 2 }}>
                <Typography>
                  Total Contacts:
                  <span className="boldNum">
                    {numberWithComma(view === 'add_contacts' ? addContactsTotal : progressTotal)}
                  </span>
                </Typography>
              </Box>
            ) : null}
            {view === 'upload_list' && activeStep === 0 && !!footerErrorMessage ? (
              <Box className="footerError" sx={{ mx: 2 }}>
                <img src={WarningIcon} alt="warning" />
                <Typography className="footerErrorText">{footerErrorMessage}</Typography>
              </Box>
            ) : null}
            <Box className="footerButtons">
              <OlcButton
                variant="primary"
                onClick={() => {
                  if (view === 'upload_list' && activeStep === 0) {
                    if (importSubStep === 2) {
                      setTriggerProgressImportSignal((p) => p + 1); // Import
                    } else {
                      setProcessFileSignal((prev) => prev + 1); // Process file (upload/map)
                    }
                  } else if (activeStep === steps.length - 1) {
                    // Require all review checkboxes to be checked (old-flow parity)
                    // Defer to child validation (isReviewValid) to avoid mismatches and flicker
                    if (!isReviewValid) {
                      setErrors((prev) => ({ ...prev, review: true }));
                      // Optional snackbar feedback to match old flow if needed
                      // dispatch(failure(MESSAGES.ORDERS.CREATE.REVIEW.REQUIRED));
                      return;
                    }

                    submitOrder();
                  } else if (activeStep === 2) {
                    // Validate cost before navigating to Review — stay and show error if API fails
                    handleOrderDetailsNext();
                  } else {
                    handleNext();
                  }
                }}
                disabled={
                  view === 'upload_list' && activeStep === 0
                    ? importSubStep === 2
                      ? !progressCanImport
                      : !isUploadListFileSelected
                    : view === 'add_contacts' && activeStep === 0
                      ? !hasContactsSelected
                      : activeStep === 1
                        ? !isMailerDetailsValid
                        : activeStep === 2
                          ? !isOrderDetailsValid || isCostValidating
                          : activeStep === steps.length - 1
                            ? orderSubmitLoader || !isReviewValid
                            : false
                }
              >
                {view === 'upload_list' && activeStep === 0 ? (
                  importSubStep === 2 ? (
                    'Import'
                  ) : (
                    'Process file'
                  )
                ) : activeStep === steps.length - 1 ? (
                  orderSubmitLoader ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Submit Order'
                  )
                ) : activeStep === 2 && isCostValidating ? (
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                ) : (
                  'Next'
                )}
              </OlcButton>
              <OlcButton variant="secondary" onClick={onCancel}>
                Cancel
              </OlcButton>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Add New Sender Profile Modal */}
      <SenderProfileModal
        openModal={isSenderProfileModalOpen}
        handleCloseModal={() => setIsSenderProfileModalOpen(false)}
        mode="add"
        onSuccess={async (newContact) => {
          try {
            // Refresh the return addresses list
            await dispatch(
              fetchReturnAddresses({
                ...DEFAULT_PAGINATION,
                isReturnAddress: true,
                excludeDncList: true,
              })
            );
            // Map and select the newly created sender profile
            if (newContact?.id) {
              const label = `${newContact.fullName}, ${newContact.address1} ${newContact?.address2 || ''} ${newContact?.city || ''}, ${newContact?.state || ''} ${newContact?.zip || ''}`;
              const createdOption = {
                id: newContact.id,
                label,
                // Ensure dropdown recognizes this as a real selected value (not placeholder)
                title: label,
              };
              setSelectedReturnedAddress(createdOption);
            }
          } catch (e) {
            // fail silently; selection can still be made manually
          } finally {
            setIsSenderProfileModalOpen(false);
          }
        }}
      />

      <PaymentMethodRequiredModal
        open={paymentGate.isGateOpen}
        onClose={closePaymentGate}
        showAchConfirmation={isImpersonating}
        achFundsConfirmed={achFundsConfirmed}
        onAchFundsConfirmedChange={setAchFundsConfirmed}
        onSubmitOrder={() => {
          paymentGate.closeGate();
          submitOrder();
        }}
      />

      <InsufficientFundsWarningModal
        open={!!lowBalanceWarning}
        balance={lowBalanceWarning?.balance}
        cost={lowBalanceWarning?.cost}
        loading={orderSubmitLoader}
        onClose={() => setLowBalanceWarning(null)}
        onConfirm={() => {
          setLowBalanceWarning(null);
          submitOrder({ skipFundsCheck: true });
        }}
      />

      {/* Cost Breakdown Modal (Campaigns + Single Mailer) */}
      {showCostBreakdown && costDetails && (
        <CampaignCostBreakdown
          template={selectedTemplate}
          open={showCostBreakdown}
          onClose={() => setShowCostBreakdown(false)}
          costDetails={
            selectionType?.id === 2 && selectedCampaign ? costDetails : singleMailerBreakdown
          }
        />
      )}
    </>
  );
};

export default OrderFlow;
